import { useUser } from '@/app/providers';
import { ConfirmModal } from '@/components/ConfirmModal';
import { CheckboxWithHoverAction, TextCell } from '@/components/tables/contentFields';
import EmptyTableView from '@/components/tables/EmptyTableView';
import { NonSortableColumnHeader } from '@/components/tables/NonSortableColumnHeader';
import Table, { TableBody, TableHeader } from '@/components/tables/TableBasics';
import TableLoadingView from '@/components/tables/TableLoadingView';
import { useDeviceModels } from '@/features/device-models';
import { useDevices, useDevicesCount } from '@/features/devices';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { deviceAssignmentKeys, useRemoveDeviceAssignment } from '../hooks';
import type { AssignableDevice, ExistingAssignmentResponse, TargetCompany } from '../types';
import AssignmentContentContainer from './assignmentContainer';

interface ReviewConfirmStepProps {
  selectedDevices: Map<string, AssignableDevice>;
  targetCompany: TargetCompany | null;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  canSave: boolean;
  showActionButtons: boolean;
}

interface ReviewRow {
  id: string;
  serialNumber: string;
  modelTypeId: string;
  isExisting: boolean;
}

export function ReviewConfirmStep({ selectedDevices, targetCompany, onSave, onCancel, isSaving, canSave, showActionButtons }: ReviewConfirmStepProps) {
  const { t } = useTranslation();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { mutate: removeAssignment, isPending: isRemoving } = useRemoveDeviceAssignment();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<ReviewRow | null>(null);

  const { data: deviceTypes } = useDeviceModels();
  const { data: existingCountData } = useDevicesCount(
    targetCompany?.id || '',
    {},
    pageSize
  );

  useEffect(() => {
    setPage(1)
  }, [targetCompany])

  const deviceTypeMap = useMemo(() => {
    if (!deviceTypes) return new Map<number, string>();
    return new Map(deviceTypes.map((dt) => [dt.id, dt.name]));
  }, [deviceTypes]);

  const selectedDevicesArray = useMemo(() => {
    return Array.from(selectedDevices.values()).map((device) => ({
      id: device.id,
      serialNumber: device.serialNumber,
      modelTypeId: device.modelTypeId,
      isExisting: false,
    }));
  }, [selectedDevices]);

  const newDevicesPage = Math.min(page, Math.ceil(selectedDevicesArray.length / pageSize));
  const query = useMemo(() => {
    const backendPage = Math.max(0, page - newDevicesPage)
    console.log("BACKEND PAGE", backendPage)

    return {
      page: backendPage,
      pageSize: pageSize,
    }
  }, [newDevicesPage, pageSize, page])

  const { data: existingData, isLoading: isLoadingExisting } = useDevices(targetCompany?.id || '',
    query
  );


  const existingDevicesArray = useMemo(() => {
    if (!existingData?.data) return [];
    return existingData.data
      .filter((device) => !selectedDevices.has(device.id))
      .map((device) => ({
        id: device.id,
        serialNumber: device.serialNumber,
        modelTypeId: device.modelTypeId,
        isExisting: true,
      }));
  }, [existingData, selectedDevices]);


  const total = selectedDevicesArray.length + (existingCountData?.total || 0);

  const paginatedRows = useMemo(() => {
    const startIndex = (page - 1) * pageSize
    const endIndex = startIndex + pageSize;
    let remaining = pageSize

    if (startIndex < selectedDevicesArray.length) {
      const rowsFromNewDevices = selectedDevicesArray.slice(startIndex, Math.min(endIndex, selectedDevicesArray.length));
      remaining -= rowsFromNewDevices.length

      if (remaining > 0) {
        return [...rowsFromNewDevices, ...existingDevicesArray.slice(0, Math.min(existingDevicesArray.length, remaining))]
      }

      return rowsFromNewDevices
    } else {
      return existingDevicesArray
    }

  }, [selectedDevicesArray, existingDevicesArray, page, pageSize]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  }, []);

  const newDevicesCount = selectedDevicesArray.length;
  const existingDevicesCount = existingCountData?.total || existingDevicesArray.length;

  function handleRemoveClick(row: ReviewRow) {
    setRemoveTarget(row);
  }

  function handleConfirmRemove() {
    if (!removeTarget || !user?.companyUuid || !targetCompany) return;

    const removedId = removeTarget.id;
    const companyId = targetCompany.id;

    removeAssignment(
      { deviceIds: [removedId], ownCompanyId: user.companyUuid, companyId },
      {
        onSuccess: () => {
          // Immediately drop the device from the cache so the row disappears without waiting for a refetch
          queryClient.setQueryData<ExistingAssignmentResponse>(
            deviceAssignmentKeys.existingAssignmentsList({ page: 1, pageSize: 1000, companyId }),
            (old) => {
              if (!old) return old;
              return { ...old, data: old.data.filter((d) => d.id !== removedId), total: old.total - 1 };
            }
          );
          toast.success(t('deviceAssignment.step3.removeSuccess'));
          setRemoveTarget(null);
        },
        onError: () => {
          toast.error(t('deviceAssignment.step3.removeError'));
        },
      }
    );
  }

  function handleCancelRemove() {
    setRemoveTarget(null);
  }

  if (!targetCompany) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <svg
          className="size-12 opacity-60 mb-4"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
          <path d="M3.6 9h16.8a1 1 0 0 1 .9 1.45l-8.4 14.48a1 1 0 0 1-1.8 0L2.7 10.45A1 1 0 0 1 3.6 9Z" />
        </svg>
        <p className="opacity-60">{t('deviceAssignment.step3.selectCompanyFirst')}</p>
      </div>
    );
  }

  return (<>
    <AssignmentContentContainer
      page={page}
      pageSize={pageSize}
      total={total}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
    >
      {/* Summary */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="size-3 rounded-full bg-primary" />
          <span className="text-onsurface">
            {t('deviceAssignment.step3.newDevices', { count: newDevicesCount })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="size-3 rounded-full bg-muted-foreground" />
          <span className="opacity-60">
            {t('deviceAssignment.step3.existingDevices', { count: existingDevicesCount })}
          </span>
        </div>
      </div>

      {/* Table */}
      {isLoadingExisting ? (
        <TableLoadingView isLoading={true} rows={5} columns={3} withHeader={true} />
      ) : paginatedRows.length === 0 ? (
        <EmptyTableView />
      ) : (
        <Table className="min-h-0" tableClassName="min-w-full grow">
          <TableHeader>
            <th scope="col" className="px-3 py-2.5 text-start w-12">
              <span className="sr-only">{t('deviceAssignment.step3.status')}</span>
            </th>
            <NonSortableColumnHeader label={t('deviceAssignment.step3.serialNumber')} minWidth="150px" />
            <NonSortableColumnHeader label={t('deviceAssignment.step3.model')} minWidth="150px" />
          </TableHeader>
          <TableBody className="divide-y divide-border">
            {paginatedRows.map((row) => (
              <tr
                key={row.id}
                onMouseEnter={() => row.isExisting && setHoveredRowId(row.id)}
                onMouseLeave={() => row.isExisting && setHoveredRowId(null)}
                className={
                  row.isExisting
                    ? hoveredRowId === row.id
                      ? 'bg-surface-darker/30'
                      : 'opacity-50 bg-surface-darker/30'
                    : 'hover:bg-surface-darker/50'
                }
              >
                {row.isExisting ? (
                  <CheckboxWithHoverAction
                    checked={true}
                    ariaLabel={t('deviceAssignment.step3.deviceIncluded')}
                    isHovered={hoveredRowId === row.id}
                    onActionClick={() => handleRemoveClick(row)}
                    actionLabel={t('deviceAssignment.step3.removeConfirm')}
                  />
                ) : (
                  <td className="size-px whitespace-nowrap px-3 py-4">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled={false}
                      className="shrink-0 border-border rounded-sm text-primary focus:ring-primary checked:border-primary bg-surface cursor-not-allowed"
                      aria-label={t('deviceAssignment.step3.deviceIncluded')}
                    />
                  </td>
                )}
                <TextCell value={row.serialNumber} />
                <TextCell value={deviceTypeMap.get(Number(row.modelTypeId)) || row.modelTypeId} />
              </tr>
            ))}
          </TableBody>
        </Table>
      )}


    </AssignmentContentContainer>
    {showActionButtons && (
      <div className="flex justify-end gap-3 mt-4 border-t border-border pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="py-2 px-4 text-sm font-medium rounded-lg border border-border bg-surface text-onsurface hover:bg-surface-darker focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!canSave || isSaving}
          className="py-2 px-4 text-sm font-medium rounded-lg bg-primary text-onprimary shadow-2xs hover:bg-primary-darker disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {isSaving ? t('common.saving') : t('common.save')}
        </button>
      </div>
    )}
    <ConfirmModal
      isOpen={removeTarget !== null}
      title={t('deviceAssignment.step3.removeTitle')}
      message={t('deviceAssignment.step3.removeMessage', {
        serialNumber: removeTarget?.serialNumber ?? '',
        companyName: targetCompany?.name ?? '',
      })}
      confirmLabel={t('deviceAssignment.step3.removeConfirm')}
      cancelLabel={t('common.cancel')}
      onConfirm={handleConfirmRemove}
      onCancel={handleCancelRemove}
      isLoading={isRemoving}
      variant="danger"
    />
  </>
  );
}
